import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, X, Folder } from 'lucide-react';
import formsData from '../data/forms.json';

function Forms() {
  const [selectedForm, setSelectedForm] = useState(null);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      setIsMobile(mobileRegex.test(navigator.userAgent) || window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const filteredForms = formsData;

  const handleViewPDF = (form) => {
    setSelectedForm(form);
    setShowPDFViewer(true);
  };

  const handleDownload = (form) => {
    const link = document.createElement('a');
    link.href = `/assets/forms/${form.form_file}`;
    link.download = form.form_file;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const closePDFViewer = () => {
    setShowPDFViewer(false);
    setSelectedForm(null);
  };

  return (
    <div>
      {/* Page Header */}
      <div className="relative bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-72 h-72 bg-white opacity-5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <Folder className="w-16 h-16" />
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold mb-4">
              படிவங்கள்
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Forms Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredForms.map((form) => (
            <div
              key={form.id}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-gray-100 hover:border-orange-300"
            >
              {/* Card Header */}
              <div className="relative bg-gradient-to-br from-orange-500 to-orange-600 p-6 text-white">
                <div className="absolute top-0 right-0 w-32 h-32 opacity-10 rounded-bl-full"></div>
                <div className="relative">
                  <div className="w-16 h-16 bg-opacity-20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <FileText className="w-8 h-8" />
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 leading-relaxed min-h-[3.5rem]">
                  {form.form_name}
                </h3>
                

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleViewPDF(form)}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    <Eye className="w-4 h-4" />
                    பார்க்க
                  </button>
                  <button
                    onClick={() => handleDownload(form)}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    <Download className="w-4 h-4" />
                    பதிவிறக்க
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredForms.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No forms available at the moment.</p>
          </div>
        )}
      </div>

      {/* PDF Viewer Modal */}
      {showPDFViewer && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[95vh] sm:h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-t-2xl">
              <div className="flex-1">
                <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                  <FileText className="w-5 h-5 sm:w-6 sm:h-6" />
                  <h2 className="text-lg sm:text-2xl font-bold truncate max-w-[200px] sm:max-w-none">
                    {selectedForm.form_name}
                  </h2>
                </div>
              </div>
              <button
                onClick={closePDFViewer}
                className="p-2 sm:p-3 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* PDF Viewer Content */}
            <div className="flex-1 overflow-auto bg-gray-100">
              {isMobile ? (
                // Mobile View - Show options to open PDF
                <div className="flex flex-col items-center justify-center min-h-[400px] p-6">
                  <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
                    <div className="bg-orange-100 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-10 h-10 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      PDF Viewer
                    </h3>
                    <p className="text-gray-600 mb-6">
                      மொபைலில் PDF ஐ பார்க்க, கீழே உள்ள பொத்தானை அழுத்தவும்
                    </p>
                    <div className="space-y-3">
                      <a
                        href={`/assets/forms/${selectedForm.form_file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors"
                      >
                        <Eye className="w-5 h-5" />
                        PDF ஐ திறக்க
                      </a>
                      <button
                        onClick={() => handleDownload(selectedForm)}
                        className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                      >
                        <Download className="w-5 h-5" />
                        PDF ஐ பதிவிறக்க
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Desktop View - Use iframe
                <div className="w-full h-full">
                  <iframe
                    src={`/assets/forms/${selectedForm.form_file}`}
                    className="w-full h-full border-0"
                    title={selectedForm.form_name}
                    onError={(e) => {
                      console.error('Iframe failed to load PDF');
                      e.target.style.display = 'none';
                      const parent = e.target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div class="flex flex-col items-center justify-center h-full p-6">
                            <div class="text-center">
                              <p class="text-red-600 mb-4">Unable to display PDF</p>
                              <a href="/assets/forms/${selectedForm.form_file}" target="_blank" class="px-6 py-2 bg-orange-500 text-white rounded-lg">Open PDF in new tab</a>
                            </div>
                          </div>
                        `;
                      }
                    }}
                  />
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-3 sm:p-4 border-t bg-gray-50 rounded-b-2xl flex justify-between items-center">
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={() => handleDownload(selectedForm)}
                  className="px-4 sm:px-6 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors flex items-center gap-2 text-sm sm:text-base"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">பதிவிறக்க</span>
                  <span className="sm:hidden">பதிவிறக்கு</span>
                </button>
                <button
                  onClick={closePDFViewer}
                  className="px-4 sm:px-6 py-2 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors text-sm sm:text-base"
                >
                  மூடு
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Forms;